// Cache for locale separator results to avoid expensive Intl.NumberFormat calls
const localeCache = new Map();

/**
 * Returns the group and decimal separators for a given locale
 * @param locale The locale string (e.g., 'en-US', 'de-DE', 'fr-FR')
 * @returns An object containing groupSeparator and decimalSeparator
 *
 * @example
 * // For US English locale
 * const { groupSeparator, decimalSeparator } = getLocaleSeparators('en-US');
 * // Returns: { groupSeparator: ',', decimalSeparator: '.' }
 *
 * // For German locale
 * const { groupSeparator, decimalSeparator } = getLocaleSeparators('de-DE');
 * // Returns: { groupSeparator: '.', decimalSeparator: ',' }
 *
 * // For French locale (uses space as group separator)
 * const { groupSeparator, decimalSeparator } = getLocaleSeparators('fr-FR');
 * // Returns: { groupSeparator: ' ', decimalSeparator: ',' }
 */
export function getLocaleSeparators(locale = 'en-US') {
  var _example$match;
  // Check cache first
  if (localeCache.has(locale)) {
    return localeCache.get(locale);
  }
  const example = new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: 2
  }).format(123456.78);
  if (!example) {
    throw new Error(`Could not determine number format for locale: ${locale}`);
  }

  // Find all non-digit characters in the formatted number
  const nonDigits = example.match(/[^0-9]/g) || [];

  // The group separator is typically the first non-digit character (if any)
  // Common group separators: comma (,), dot (.), space ( ), thin space, non-breaking space
  const groupSeparator = nonDigits[0] || ',';

  // The decimal separator is the last non-digit character before the fractional digits
  // Look for pattern: [separator][exactly 2 digits at end]
  const decimalSeparator = ((_example$match = example.match(/[^0-9]([0-9]{2})$/)) === null || _example$match === void 0 || (_example$match = _example$match[0]) === null || _example$match === void 0 ? void 0 : _example$match[0]) || '.';
  const result = {
    groupSeparator,
    decimalSeparator
  };

  // Cache the result
  localeCache.set(locale, result);
  return result;
}
export function getLocaleDateFormatter(locale = 'en-US', timeZone) {
  return date => date.toLocaleDateString(locale, {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Clears the locale separator cache. Useful for testing or if locale preferences change.
 */
export function clearLocaleSeparatorCache() {
  localeCache.clear();
}